// src/components/Layout.tsx
import { cn } from "@/lib/utils";
import { Sidebar } from "./Sidebar/index";
import { CollapseButton } from "./Sidebar/CollapseButton";
import { useLocation } from "react-router-dom";
import { DismissibleBanner } from "@/components/shared/DismissibleBanner";
import { SUPPORT_EMAIL } from "@/lib/support";

interface LayoutProps {
  children: React.ReactNode;
  className?: string;
}

export function Layout({ children, className }: LayoutProps) {
  const location = useLocation();
  const currentPath = location.pathname;

  // Pages that own their layout/padding (so Layout adds no px-6 py-4 frame).
  // These pages each supply their own horizontal gutter; the global catalog-floor
  // texture shows through their (transparent) backgrounds.
  const isFullHeightPage =
    currentPath === "/home" ||
    currentPath === "/tracks" ||
    currentPath === "/albums" ||
    currentPath === "/artists" ||
    currentPath.startsWith("/publishers") ||
    currentPath === "/drafts" ||
    currentPath === "/upload" ||
    // Every storefront page (the list, a storefront, its browse and detail pages)
    // draws its own background and scrolls itself. Framed, the shell's floor
    // showed as strips down the left and along the bottom with a square corner
    // by the sidebar button (owner, 2026-10-11: "the ui cracks in the corner").
    currentPath.startsWith("/marketplace") ||
    currentPath === "/search" ||
    currentPath === "/search-legacy" ||
    currentPath.startsWith("/licenses") ||
    currentPath.startsWith("/projects") ||
    currentPath === "/request-song";

  // Your companies fills the window on desktop (its header stays put and two panes scroll on their
  // own), so there it owns its frame like the pages above. On phone it is one scroller and keeps the
  // shared frame unchanged.
  const ownsDesktopFrame =
    currentPath === "/corporate-hierarchy" || currentPath.startsWith("/corporate-hierarchy/");

  const isAuthPage = currentPath.startsWith("/auth/");

  if (isAuthPage) {
    return (
      <div
        className={cn(
          "min-h-screen w-full flex flex-col overflow-hidden bg-background",
          className
        )}
      >
        {children}
      </div>
    );
  }

  return (
    <div className={cn("catalog-floor relative h-screen w-full flex flex-col overflow-hidden", className)}>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:top-2 focus:left-2 focus:bg-background focus:text-foreground focus:px-3 focus:py-2 focus:rounded-md"
      >
        Skip to content
      </a>
      <DismissibleBanner
        storageKey="beta-banner-dismissed"
        storage="session"
        className="bg-gradient-to-r from-purple-700/10 to-purple-900/10 border-b border-purple-700/20 px-4 py-1.5 text-center text-xs text-purple-800 dark:text-purple-300"
        dismissButtonClassName="hover:bg-purple-500/10 text-purple-500 dark:text-purple-400"
      >
        <span>
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-purple-500 dark:bg-purple-400 mr-1.5 align-middle shadow-[0_0_4px_1px_rgba(147,51,234,0.5)] dark:shadow-[0_0_4px_1px_rgba(192,132,252,0.5)]" />
          The app is currently in beta phase — please bear with us as we iron out the bugs. For any feedback, reach out to{" "}
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="underline font-medium hover:text-purple-900 dark:hover:text-purple-100"
          >
            {SUPPORT_EMAIL}
          </a>
        </span>
      </DismissibleBanner>
      <div className="flex-1 flex min-h-0 relative">
        <Sidebar />
        <CollapseButton />
        <main id="main-content" className="flex-1 flex flex-col min-w-0 overflow-hidden">
          <div className={cn(
            "flex-1 flex flex-col min-h-0",
            // Full-height pages own their scroller. Every OTHER page gets one here: `main` hides
            // overflow, so without it a page taller than the window — /search/history,
            // /rights-to-confirm, /admin/rights-review — was clipped with no way to reach the rest.
            // Pages that already scroll themselves (`flex-1 min-h-0 overflow-y-auto`) fill this box
            // exactly, so it never overflows for them and no second scrollbar appears.
            isFullHeightPage
              ? ""
              : ownsDesktopFrame
                ? "max-phone:px-6 max-phone:py-4 max-phone:overflow-y-auto"
                : "px-6 py-4 overflow-y-auto"
          )}>
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
